import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  LoadingOverlay,
  Menu,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconChartBar,
  IconChevronDown,
  IconDownload,
  IconFileTypePdf,
  IconRefresh,
} from '@tabler/icons-react';
import type {
  PaymentMethod,
  ReportByMethodResponse,
  ReportByRequirementResponse,
  ReportByStudentResponse,
  ReportFilters,
  ReportResponseBase,
  ReportRowStatus,
  ReportView,
} from '@pta/types';
import { ALL_PAYMENT_METHODS, ALL_REPORT_ROW_STATUSES } from '@pta/types';
import { formatMoney } from '@pta/i18n/format';
import {
  fetchByMethodReport,
  fetchByRequirementReport,
  fetchByStudentReport,
} from '../api/reports';
import { ApiError } from '../api/client';
import { toIsoDate } from '../util/format';
import {
  byMethodCsv,
  byMethodPdf,
  byRequirementCsv,
  byRequirementPdf,
  byStudentCsv,
  byStudentPdf,
  CSV_SEPARATOR_OPTIONS,
  type CsvSeparator,
  downloadCsv,
  PM_LABEL,
  STATUS_COLOR,
  STATUS_LABEL,
} from '../util/reports-export';
import { useAuth } from '../auth/AuthProvider';

/**
 * Riport oldal — három tab (by-student / by-requirement / by-method) közös
 * filter-bárral. A filter változás utáni "Lekérdezés" gombra fut a BE hívás.
 *
 * Pénzformázás: a BE response `numberFormat` + `currencySymbol` + `currencyMinorUnit`
 * mezőit használja (riport-specifikus formázás, függetlenül az aktív csoport
 * beállításaitól, mert egy riport mindig a lekérdezett csoportról szól).
 */

type StatusFilter = ReportRowStatus | 'all';
type MethodFilter = PaymentMethod | 'all';

/** Riport-response → pénzformázó closure. */
function makeMoneyFormatter(resp: ReportResponseBase) {
  return (value: string | number) =>
    formatMoney(value, resp.numberFormat, resp.currencySymbol, resp.currencyMinorUnit);
}

export function ReportsPage() {
  const { activeGroupId, myGroups } = useAuth();
  const activeGroup = useMemo(
    () => myGroups.find((g) => g.id === activeGroupId) ?? null,
    [myGroups, activeGroupId],
  );

  const [paidRange, setPaidRange] = useState<[Date | null, Date | null]>([null, null]);
  const [dueRange, setDueRange] = useState<[Date | null, Date | null]>([null, null]);
  const [paymentMethod, setPaymentMethod] = useState<MethodFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');

  const [view, setView] = useState<ReportView>('by-student');
  const [byStudent, setByStudent] = useState<ReportByStudentResponse | null>(null);
  const [byReq, setByReq] = useState<ReportByRequirementResponse | null>(null);
  const [byMethod, setByMethod] = useState<ReportByMethodResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filters: ReportFilters = useMemo(
    () => ({
      paidFrom: paidRange[0] ? toIsoDate(paidRange[0]) : null,
      paidTo: paidRange[1] ? toIsoDate(paidRange[1]) : null,
      dueFrom: dueRange[0] ? toIsoDate(dueRange[0]) : null,
      dueTo: dueRange[1] ? toIsoDate(dueRange[1]) : null,
      paymentMethod: paymentMethod === 'all' ? null : paymentMethod,
      status: status === 'all' ? null : status,
    }),
    [paidRange, dueRange, paymentMethod, status],
  );

  async function runQuery() {
    if (!activeGroupId) return;
    setLoading(true);
    setError(null);
    try {
      if (view === 'by-student') {
        setByStudent(await fetchByStudentReport(activeGroupId, filters));
      } else if (view === 'by-requirement') {
        setByReq(await fetchByRequirementReport(activeGroupId, filters));
      } else {
        setByMethod(await fetchByMethodReport(activeGroupId, filters));
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      setError(msg);
      notifications.show({ color: 'red', title: 'Riport lekérés sikertelen', message: msg });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (activeGroupId) {
      void runQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId, view]);

  if (!activeGroup) {
    return (
      <Alert color="yellow">
        Válassz csoportot a fejlécben a riportok megjelenítéséhez.
      </Alert>
    );
  }

  const filenameBase = `riport-${activeGroup.name}`;

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Group>
          <IconChartBar size={28} />
          <Title order={2}>Riportok</Title>
          <Text c="dimmed">— {activeGroup.name}</Text>
        </Group>
      </Group>

      <Card withBorder>
        <Group align="flex-end" wrap="wrap">
          <DatePickerInput
            type="range"
            label="Befizetés időszaka"
            placeholder="Bármikor"
            value={paidRange}
            onChange={(v) => setPaidRange(v as [Date | null, Date | null])}
            clearable
            maw={260}
          />
          <DatePickerInput
            type="range"
            label="Esedékesség időszaka"
            placeholder="Bármikor"
            value={dueRange}
            onChange={(v) => setDueRange(v as [Date | null, Date | null])}
            clearable
            maw={260}
          />
          <Select
            label="Fizetési mód"
            value={paymentMethod}
            onChange={(v) => setPaymentMethod((v as MethodFilter) ?? 'all')}
            data={[
              { value: 'all', label: 'Bármelyik' },
              ...ALL_PAYMENT_METHODS.map((pm) => ({ value: pm, label: PM_LABEL[pm] })),
            ]}
            maw={180}
          />
          <Select
            label="Státusz"
            value={status}
            onChange={(v) => setStatus((v as StatusFilter) ?? 'all')}
            disabled={view === 'by-method'}
            data={[
              { value: 'all', label: 'Bármilyen' },
              ...ALL_REPORT_ROW_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
            ]}
            maw={180}
          />
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={() => void runQuery()}
            loading={loading}
          >
            Lekérdezés
          </Button>
        </Group>
      </Card>

      <Tabs value={view} onChange={(v) => v && setView(v as ReportView)}>
        <Tabs.List>
          <Tabs.Tab value="by-student">Diákonként</Tabs.Tab>
          <Tabs.Tab value="by-requirement">Előírásonként</Tabs.Tab>
          <Tabs.Tab value="by-method">Fizetési mód szerint</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="by-student" pt="md">
          <ReportPanel
            loading={loading}
            error={error}
            response={byStudent}
            renderTable={(r) => <ByStudentTable data={r} />}
            csvBuilder={byStudentCsv}
            pdfBuilder={byStudentPdf}
            filenameBase={filenameBase + '-diak'}
          />
        </Tabs.Panel>
        <Tabs.Panel value="by-requirement" pt="md">
          <ReportPanel
            loading={loading}
            error={error}
            response={byReq}
            renderTable={(r) => <ByRequirementTable data={r} />}
            csvBuilder={byRequirementCsv}
            pdfBuilder={byRequirementPdf}
            filenameBase={filenameBase + '-eloiras'}
          />
        </Tabs.Panel>
        <Tabs.Panel value="by-method" pt="md">
          <ReportPanel
            loading={loading}
            error={error}
            response={byMethod}
            renderTable={(r) => <ByMethodTable data={r} />}
            csvBuilder={byMethodCsv}
            pdfBuilder={byMethodPdf}
            filenameBase={filenameBase + '-mod'}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

// ============================================================
// Közös panel
// ============================================================

interface ReportPanelProps<T> {
  loading: boolean;
  error: string | null;
  response: T | null;
  renderTable: (r: T) => React.ReactNode;
  csvBuilder: (r: T, sep: CsvSeparator) => string;
  pdfBuilder: (r: T, filename: string) => Promise<void>;
  filenameBase: string;
}

function ReportPanel<T>({
  loading,
  error,
  response,
  renderTable,
  csvBuilder,
  pdfBuilder,
  filenameBase,
}: ReportPanelProps<T>) {
  function handleCsv(sep: CsvSeparator) {
    if (!response) return;
    const csv = csvBuilder(response, sep);
    downloadCsv(csv, `${filenameBase}.csv`);
  }

  async function handlePdf() {
    if (!response) return;
    try {
      await pdfBuilder(response, `${filenameBase}.pdf`);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'PDF generálás sikertelen',
        message: String(err),
      });
    }
  }

  return (
    <Stack pos="relative">
      <LoadingOverlay visible={loading} />
      {error && <Alert color="red">{error}</Alert>}
      {response && (
        <Group>
          <Menu shadow="md" position="bottom-start">
            <Menu.Target>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                rightSection={<IconChevronDown size={14} />}
              >
                CSV export
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Elválasztó</Menu.Label>
              {CSV_SEPARATOR_OPTIONS.map((opt) => (
                <Menu.Item key={opt.value} onClick={() => handleCsv(opt.value)}>
                  {opt.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          <Button
            variant="light"
            leftSection={<IconFileTypePdf size={16} />}
            onClick={() => void handlePdf()}
          >
            PDF export
          </Button>
        </Group>
      )}
      {response && renderTable(response)}
    </Stack>
  );
}

// ============================================================
// Tables
// ============================================================

function ByStudentTable({ data }: { data: ReportByStudentResponse }) {
  const money = makeMoneyFormatter(data);
  return (
    <Card withBorder p={0}>
      <Table striped highlightOnHover withTableBorder={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Diák</Table.Th>
            <Table.Th ta="right">Várt</Table.Th>
            <Table.Th ta="right">Befizetve</Table.Th>
            <Table.Th ta="right">Hátralék</Table.Th>
            <Table.Th ta="right">Targets</Table.Th>
            <Table.Th>Státusz</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.rows.map((r) => (
            <Table.Tr key={r.studentId}>
              <Table.Td>
                {r.lastName} {r.firstName}
              </Table.Td>
              <Table.Td ta="right">{money(r.expectedTotal)}</Table.Td>
              <Table.Td ta="right">{money(r.paidTotal)}</Table.Td>
              <Table.Td ta="right">{money(r.remaining)}</Table.Td>
              <Table.Td ta="right">
                {r.targetCount}
                {r.waivedCount > 0 ? ` (${r.waivedCount} mentesítve)` : ''}
              </Table.Td>
              <Table.Td>
                <Badge color={STATUS_COLOR[r.status]}>{STATUS_LABEL[r.status]}</Badge>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
        <Table.Tfoot>
          <Table.Tr>
            <Table.Th>Összesen ({data.totals.studentCount} diák)</Table.Th>
            <Table.Th ta="right">{money(data.totals.expectedTotal)}</Table.Th>
            <Table.Th ta="right">{money(data.totals.paidTotal)}</Table.Th>
            <Table.Th ta="right">{money(data.totals.remaining)}</Table.Th>
            <Table.Th />
            <Table.Th />
          </Table.Tr>
        </Table.Tfoot>
      </Table>
    </Card>
  );
}

function ByRequirementTable({ data }: { data: ReportByRequirementResponse }) {
  const money = makeMoneyFormatter(data);
  return (
    <Card withBorder p={0}>
      <Table striped highlightOnHover withTableBorder={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Előírás</Table.Th>
            <Table.Th>Esedékes</Table.Th>
            <Table.Th ta="right">Várt</Table.Th>
            <Table.Th ta="right">Befizetve</Table.Th>
            <Table.Th ta="right">Hátralék</Table.Th>
            <Table.Th ta="right">Targets</Table.Th>
            <Table.Th>Státusz</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.rows.map((r) => (
            <Table.Tr key={r.requirementId}>
              <Table.Td>
                <Stack gap={0}>
                  <Text fw={500}>{r.title}</Text>
                  <Text size="xs" c="dimmed">
                    {r.purpose}
                  </Text>
                </Stack>
              </Table.Td>
              <Table.Td>{r.dueDate ?? '—'}</Table.Td>
              <Table.Td ta="right">{money(r.expectedTotal)}</Table.Td>
              <Table.Td ta="right">{money(r.paidTotal)}</Table.Td>
              <Table.Td ta="right">{money(r.remaining)}</Table.Td>
              <Table.Td ta="right">
                <Text size="xs">
                  {r.paidTargetCount}/{r.targetCount}
                  {r.waivedTargetCount > 0 ? ` (${r.waivedTargetCount}M)` : ''}
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge color={STATUS_COLOR[r.rowStatus]}>{STATUS_LABEL[r.rowStatus]}</Badge>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
        <Table.Tfoot>
          <Table.Tr>
            <Table.Th>Összesen ({data.totals.requirementCount} előírás)</Table.Th>
            <Table.Th />
            <Table.Th ta="right">{money(data.totals.expectedTotal)}</Table.Th>
            <Table.Th ta="right">{money(data.totals.paidTotal)}</Table.Th>
            <Table.Th ta="right">{money(data.totals.remaining)}</Table.Th>
            <Table.Th />
            <Table.Th />
          </Table.Tr>
        </Table.Tfoot>
      </Table>
    </Card>
  );
}

function ByMethodTable({ data }: { data: ReportByMethodResponse }) {
  const money = makeMoneyFormatter(data);
  return (
    <Card withBorder p={0}>
      <Table striped highlightOnHover withTableBorder={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Fizetési mód</Table.Th>
            <Table.Th ta="right">Befizetések száma</Table.Th>
            <Table.Th ta="right">Összeg</Table.Th>
            <Table.Th ta="right">Targets</Table.Th>
            <Table.Th ta="right">Előírások</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.rows.map((r) => (
            <Table.Tr key={r.paymentMethod}>
              <Table.Td>{PM_LABEL[r.paymentMethod]}</Table.Td>
              <Table.Td ta="right">{r.paymentCount}</Table.Td>
              <Table.Td ta="right">{money(r.paidTotal)}</Table.Td>
              <Table.Td ta="right">{r.targetCount}</Table.Td>
              <Table.Td ta="right">{r.requirementCount}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
        <Table.Tfoot>
          <Table.Tr>
            <Table.Th>Összesen</Table.Th>
            <Table.Th ta="right">{data.totals.paymentCount}</Table.Th>
            <Table.Th ta="right">{money(data.totals.paidTotal)}</Table.Th>
            <Table.Th />
            <Table.Th />
          </Table.Tr>
        </Table.Tfoot>
      </Table>
    </Card>
  );
}
